// ADDED: the bell in the nav bar. It shows how many friend requests are waiting for you,
// and tapping it opens the Friends page, where they are answered.
//
// The count is refreshed when the bell first appears, whenever you move to another page,
// once a minute, and straight away after you answer a request on the Friends page (which
// announces it with a "friends-changed" event), so the badge never lags behind.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import axios from '../../api/axios';

const REFRESH_EVERY_MS = 60 * 1000;
export const FRIENDS_CHANGED_EVENT = 'friends-changed';

export default function NotificationBell({ onNavigate }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [pending, setPending] = useState(0);

    const refresh = useCallback(() => {
        axios.get('/friends/requests/count')
            .then((response) => setPending(response.data.pending || 0))
            // A failed check just leaves the last known count - the bell is never worth an error
            .catch(() => {});
    }, []);

    // First load, and again on every page change
    useEffect(() => { refresh(); }, [refresh, location.pathname]);

    // Once a minute, plus immediately when the Friends page reports a change
    useEffect(() => {
        const timer = setInterval(refresh, REFRESH_EVERY_MS);
        window.addEventListener(FRIENDS_CHANGED_EVENT, refresh);
        return () => {
            clearInterval(timer);
            window.removeEventListener(FRIENDS_CHANGED_EVENT, refresh);
        };
    }, [refresh]);

    // The message the bell carries, used for the tooltip and for screen readers
    const message = pending === 0
        ? 'No friend requests waiting'
        : `${pending} friend ${pending === 1 ? 'request' : 'requests'} waiting for you`;

    return (
        <button
            type="button"
            onClick={() => (onNavigate ? onNavigate('/friends') : navigate('/friends'))}
            title={message}
            aria-label={message}
            className="relative p-2 rounded-lg cv-body hover:text-brand-flame hover:bg-brand-flame/10 transition duration-300">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
            </svg>
            {/* The badge only exists when something is waiting */}
            {pending > 0 &&
                <span aria-hidden="true"
                      className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-brand-flame text-white
                                 font-display font-bold text-[0.65rem] leading-[1.1rem] text-center shadow">
                    {pending > 9 ? '9+' : pending}
                </span>
            }
        </button>
    );
}
